import {
  deleteV1CompaniesCompanyIdFuelSurchargeSchedulePricesId,
  getV1CompaniesCompanyIdFuelSurchargeSchedulesFuelSurchargeScheduleIdFuelSurchargeSchedulePrices,
  patchV1CompaniesCompanyIdFuelSurchargeSchedulePricesId,
  postV1CompaniesCompanyIdFuelSurchargeSchedulesFuelSurchargeScheduleIdFuelSurchargeSchedulePrices,
} from '@treadinc/horizon-api-spec';
import { useState } from 'react';

import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FuelSurchargeSchedulePrice } from './model';

interface GetFuelSurchargeSchedulePricesParams {
  fuelSurchargeScheduleId: string;
  limit?: number;
  cursor?: string;
  link?: PaginationLink;
  signal?: AbortSignal;
}

interface CreateFuelSurchargeSchedulePriceParams {
  fuelSurchargeScheduleId: string;
  price: number;
  billingSurcharge: number;
}

interface UpdateFuelSurchargeSchedulePriceParams {
  id: string;
  price: number;
  billingSurcharge: number;
}

export function useFuelSurchargeSchedulePrices() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getFuelSurchargeSchedulePrices = async ({
    fuelSurchargeScheduleId,
    limit = 20,
    cursor,
    link,
    signal,
  }: GetFuelSurchargeSchedulePricesParams) => {
    const query = {
      'page[limit]': limit,
      ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
    };

    try {
      const response =
        await getV1CompaniesCompanyIdFuelSurchargeSchedulesFuelSurchargeScheduleIdFuelSurchargeSchedulePrices(
          {
            path: {
              'company-id': companyId,
              'fuel-surcharge-schedule-id': fuelSurchargeScheduleId,
            },
            query,
            signal,
          },
        );
      const parsedData = response.data.data.map(FuelSurchargeSchedulePrice.parse);
      const pagination = extractPagination(response);
      return { data: parsedData, pagination };
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(error);
      throw error;
    }
  };

  const createFuelSurchargeSchedulePrice = async ({
    fuelSurchargeScheduleId,
    price,
    billingSurcharge,
  }: CreateFuelSurchargeSchedulePriceParams) => {
    try {
      setIsCreating(true);
      const response =
        await postV1CompaniesCompanyIdFuelSurchargeSchedulesFuelSurchargeScheduleIdFuelSurchargeSchedulePrices(
          {
            path: {
              'company-id': companyId,
              'fuel-surcharge-schedule-id': fuelSurchargeScheduleId,
            },
            body: {
              price,
              bill_surcharge: billingSurcharge,
              bill_surcharge_percentage: billingSurcharge,
            },
          },
        );
      return FuelSurchargeSchedulePrice.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const updateFuelSurchargeSchedulePrice = async ({
    id,
    price,
    billingSurcharge,
  }: UpdateFuelSurchargeSchedulePriceParams) => {
    try {
      setIsUpdating(true);
      const response = await patchV1CompaniesCompanyIdFuelSurchargeSchedulePricesId({
        path: { 'company-id': companyId, id },
        body: { price, bill_surcharge: billingSurcharge },
      });
      return FuelSurchargeSchedulePrice.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteFuelSurchargeSchedulePrice = async (id: string) => {
    try {
      setIsDeleting(true);
      await deleteV1CompaniesCompanyIdFuelSurchargeSchedulePricesId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    getFuelSurchargeSchedulePrices,
    createFuelSurchargeSchedulePrice,
    updateFuelSurchargeSchedulePrice,
    deleteFuelSurchargeSchedulePrice,
    isCreating,
    isUpdating,
    isDeleting,
  };
}
